import { Box, Stack, Typography } from '@mui/material';
import { BetWithMarketOutcomeEvent } from '../../@types';
import { getReofferedColor } from '../../utils';
import { BetDetails } from '../atoms/BetDetails';
import OddsDisplay from '../atoms/OddsDisplay';
import { useMarketplaceConfig } from '../../queries/marketplace';

import { mergeSx } from '../../utils/styles';

const styles = {
  infoRow: { justifyContent: 'space-between', flexGrow: 1 },
  selectionDetails: { flexGrow: 1, textAlign: 'left', pr: 1, pl: 0.5 },
  outcomeRow: { alignItems: 'center' },
  previousOdds: { height: 'fit-content', color: 'neutral.700' },

  color: (color: string) => ({ color }),
  waysEvent: {
    position: 'relative',
    '::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      bottom: 0,
      left: 0,
      width: '5px',
      backgroundColor: 'primary.main',
    },
  },
  betInfo: { py: 1, px: 1.5 },
  oddsContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
  },
  details: {
    color: 'neutral.600',
  },
  previousValues: {
    textDecoration: 'line-through',
    opacity: 0.5,
  },
  bankerContainer: {
    display: 'grid',
    placeItems: 'center',
  },
  banker: {
    display: 'grid',
    placeItems: 'center',
    color: 'neutral.600',
    borderRadius: 0,
    height: 48,
    width: 48,
    backgroundColor: 'white',
    fontWeight: 600,
    pl: 1,
  },
  selectedBanker: {
    color: 'white',
    backgroundColor: 'info.700',
  },
} as const;

type ReofferedBetslipItemProps = {
  bet: BetWithMarketOutcomeEvent;
  previousBet?: BetWithMarketOutcomeEvent;
  isWaysEvent: boolean;
  bets: BetWithMarketOutcomeEvent[];
  showBankers: boolean;
};

const ReofferedBetslipItem = ({ bet, previousBet, isWaysEvent, showBankers }: ReofferedBetslipItemProps) => {
  const isBanker = bet.banker || false;

  const { isFTBFeed } = useMarketplaceConfig();

  const { color: oddsColor, changed: oddsChanged } = getReofferedColor(bet.odds, previousBet?.odds);
  const { color: specialValuesColor, changed: specialValuesChanged } = getReofferedColor(
    isFTBFeed ? bet.outcome.properties?.specialValue : bet.specialValues,
    isFTBFeed ? previousBet?.outcome.properties?.specialValue : previousBet?.specialValues
  );

  const showPreviousOutcome = isFTBFeed && specialValuesChanged && previousBet;
  const showPreviousSpecialValues = specialValuesChanged && previousBet;
  const showPreviousOdds = oddsChanged && previousBet;

  return (
    <Stack direction='row' sx={isWaysEvent ? styles.waysEvent : undefined}>
      <Stack direction='row' sx={mergeSx(styles.infoRow, styles.betInfo)}>
        <Stack spacing={0.5} sx={styles.selectionDetails}>
          <Stack spacing={0.5} direction='row' sx={styles.outcomeRow}>
            {/* FIXME: We show the previous and changed whole outcome name, since FTB does not have SVs */}
            {showPreviousOutcome && (
              <BetDetails.Outcome
                outcomeName={previousBet.outcome.name}
                outcomeShortName={previousBet.outcome.shortName}
                {...previousBet.outcome.properties}
                variant='subtitle2'
                sx={mergeSx(styles.details, styles.previousValues)}
                marketName={previousBet.market.name}
                marketTypeName={previousBet.market.marketType.name}
                marketDisplayType={previousBet.market.displayType}
                eventName={previousBet.event.name}
              />
            )}
            <BetDetails.Outcome
              outcomeName={bet.outcome.name}
              outcomeShortName={bet.outcome.shortName}
              {...bet.outcome.properties}
              variant='subtitle2'
              sx={styles.color(isFTBFeed ? specialValuesColor : 'neutral.600')}
              marketName={bet.market.name}
              marketTypeName={bet.market.marketType.name}
              marketDisplayType={bet.market.displayType}
              eventName={bet.event.name}
            />
            {/* OM specific handling, will not render on FTB since specialValues will be an empty array*/}
            {showPreviousSpecialValues && (
              <BetDetails.SpecialValues
                outcomeName={previousBet.outcome.name}
                specialValues={previousBet.specialValues}
                marketTypeName={previousBet.market.marketType.name}
                specialValueModel={previousBet.specialValues?.[0]?.model}
                sportId={previousBet.market.marketType.sportId}
                variant='subtitle2'
                sx={mergeSx(styles.details, styles.previousValues)}
              />
            )}
            <BetDetails.SpecialValues
              outcomeName={bet.outcome.name}
              specialValues={bet.specialValues}
              marketTypeName={bet.market.marketType.name}
              specialValueModel={bet.specialValues?.[0]?.model}
              sportId={bet.market.marketType.sportId}
              sx={styles.color(specialValuesColor)}
              variant='subtitle2'
            />
          </Stack>
          <Typography variant='h6' sx={styles.details}>
            {bet.event.name}
          </Typography>
          <BetDetails.Market market={bet.market} sx={styles.details} />
        </Stack>
        <Box sx={styles.oddsContainer}>
          {showPreviousOdds && (
            <OddsDisplay
              value={previousBet.odds}
              variant='subtitle2'
              sx={mergeSx(styles.previousOdds, styles.previousValues)}
            />
          )}
          <OddsDisplay value={bet.odds} variant='subtitle2' sx={styles.color(oddsColor)} />
        </Box>
      </Stack>
      {showBankers && (
        <Box sx={styles.bankerContainer}>
          <Box sx={mergeSx(styles.banker, isBanker && styles.selectedBanker)}>
            <Typography variant='h5'>B</Typography>
          </Box>
        </Box>
      )}
    </Stack>
  );
};

export default ReofferedBetslipItem;
